"use client"

import React from 'react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/fun-ui/Button";

import { useGuides, usePagination, useStore, useSubjects } from "@/stores/appStore"
import { Alert } from "@/components/fun-ui/Alert";
import { ChevronRightIcon, DeleteIcon, RemoveIcon } from "@/components/fun-ui/icons";
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DEFAULT_PAGE_SIZE_LIST } from '@/config/const';
import { ForReferenceOnly } from '@/components/dashboard/ForReferenceOnly';
import { useSelection } from '@/hooks/useSelection';
import { ResetIcon } from '@radix-ui/react-icons';
import { Tip } from '@/components/fun-ui/Tip';
import {
  VipRenewalIncentiveFooterAd,
  useVipRenewalIncentiveFooterAdState,
} from '@/components/payment/VipRenewalIncentiveFooterAd';


interface TableFooterbar {
  className?: string;
  subjectId?: string;
  onTurnPage?: (to: string, pageIndex: number) => void;
}
export default function TableFooterbar({
  className = '',
  subjectId = '',
  onTurnPage = () => { }
}: TableFooterbar) {
  const {
    totalCounts,
    pageIndex,
    pageSize,
    setPageSize,
    previousPage,
    getCanPreviousPage,
    nextPage,
    getCanNextPage,
    getPageCount,
  } = usePagination(subjectId);
  const {
    selectedIds,
    resetRowSelection,
  } = useSelection(subjectId);
  const [deletePapers] = useStore((state) => [
    state.deletePapers
  ]);
  const { removePaper } = useSubjects();
  const { touchpointSlots, dismissTouchpoint } = useGuides();
  const vipFooterAdItem = (
    touchpointSlots.footerAd?.type === 'first_offer'
    || touchpointSlots.footerAd?.type === 'recovery_offer'
  ) ? touchpointSlots.footerAd : null;
  const vipModalVisible = touchpointSlots.modal?.type === 'first_offer'
    || touchpointSlots.modal?.type === 'recovery_offer';
  const vipFooterAdState = useVipRenewalIncentiveFooterAdState(
    vipFooterAdItem,
    async (item) => {
      await dismissTouchpoint(item);
    },
    true,
  );
  const showVipFooterAd = !vipModalVisible && vipFooterAdState.visible;

  return (
    <div
      className={cn(
        "relative transition-all",
        "duration-300 min-h-9 h-fit",
        "flex flex-row justify-between items-center",
        showVipFooterAd ? "overflow-visible" : "overflow-hidden",
        className,
      )}
    >
      <div className="flex flex-1 items-center select-none">
        <div className="min-w-[160px]">
          <div className="text-sm">
            已选中 {selectedIds.length} 行
            （共 {totalCounts} 行，支持跨页勾选）
          </div>
          <ForReferenceOnly className="mt-1 block" />
        </div>
        {
          !!selectedIds.length ? (
            <Tip label="重置选中的行">
              <Button
                variant="ghost"
                size="sm"
                className="space-x-1 font-normal"
                onClick={() => {
                  resetRowSelection();
                }}
              ><ResetIcon /><span>重置</span></Button>
            </Tip>
          ) : null
        }

        {subjectId && !!selectedIds.length ? (
          <Alert
            title={`移除文献`}
            description={(
              <span>
                确认将所选的
                <span className="text-destructive">
                  &nbsp;{selectedIds.length}&nbsp;篇
                </span>
                文献从主题分类中移除吗？
              </span>
            )}
            confirmLabel="移除"
            confirmVariant="destructive"
            confirm={() => {
              removePaper(subjectId, selectedIds);
              resetRowSelection();
            }}>
            <Button
              variant="ghost"
              size="sm"
              className="space-x-1 text-destructive hover:text-destructive mr-2"
            ><RemoveIcon /><span>移除</span></Button>
          </Alert>
        ) : null}
        {!!selectedIds.length ? (
          <Alert
            title={`删除文献`}
            description={(
              <span>
                确认删除所选的
                <span className="text-destructive">
                  &nbsp;{selectedIds.length}&nbsp;篇
                </span>文献吗？
              </span>
            )}
            confirmLabel="删除"
            confirmVariant="destructive"
            confirm={async () => {
              await deletePapers(selectedIds, subjectId);
              resetRowSelection();
            }}>
            <Button
              variant="ghost"
              size="sm"
              className="space-x-1 text-destructive hover:text-destructive"
            > <DeleteIcon /><span>删除</span></Button>
          </Alert>
        ) : null}
      </div>
      {showVipFooterAd ? (
        <VipRenewalIncentiveFooterAd state={vipFooterAdState} />
      ) : null}
      <div className="flex gap-8 select-none">
        <div className="flex w-fit items-center gap-2">
          <Label htmlFor="rows-per-page" className="text-sm font-normal">
            每页
          </Label>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value))
            }}
          >
            <SelectTrigger className="w-fit" id="rows-per-page">
              <SelectValue
                placeholder={pageSize.toString()}
              />
            </SelectTrigger>
            <SelectContent side="top">
              {DEFAULT_PAGE_SIZE_LIST.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label htmlFor="rows-per-page" className="text-sm font-normal">
            行
          </Label>
        </div>
        <div className="flex w-fit items-center justify-center text-sm">
          {pageIndex + 1} / {getPageCount()}
        </div>
        <div className="flex  w-fit items-center gap-2">
          <Button
            variant="outline"
            className="size-8"
            size="icon"
            onClick={() => {
              previousPage();
              onTurnPage?.('prev', pageIndex);

            }}
            disabled={!getCanPreviousPage()}
          >
            <span className="sr-only">上一页</span>
            <ChevronRightIcon className="rotate-180" />
          </Button>
          <Button
            variant="outline"
            className="size-8"
            size="icon"
            onClick={() => {
              nextPage();
              onTurnPage?.('next', pageIndex);
            }}
            disabled={!getCanNextPage()}
          >
            <span className="sr-only">下一页</span>
            <ChevronRightIcon />
          </Button>

        </div>
      </div>
    </div>
  )
}
